import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Row, Col, Form, Input, Checkbox, message, Popconfirm, Badge, Divider, Spin } from 'antd';
import { 
  UserOutlined, 
  UserAddOutlined, 
  DeleteOutlined, 
  TeamOutlined,
  LockOutlined,
  EditOutlined,
  MailOutlined
} from '@ant-design/icons';
import axios from 'axios';
import EditBoxModal from './EditBoxModal';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalLoading, setModalLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form] = Form.useForm();

  const CARD_PADDING = '10px 20px'; 
  const USERS_URL = '/api/users/manage/';

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(USERS_URL);
      setUsers(response.data);
    } catch (error) {
      message.error('Error loading users');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserDetails = async (user) => {
    setEditingUser(user);
    setIsModalVisible(true);
    setModalLoading(true);
    try {
      // Zahtev backendu za specifičnog usera preko ID-a
      const response = await axios.get(`${USERS_URL}${user.id}/`);
      const userData = response.data;
      
      form.setFieldsValue({
        username: userData.username,
        first_name: userData.first_name,
        last_name: userData.last_name,
        email: userData.email,
        ...userData.perms
      });
    } catch (error) {
      message.error('Error fetching user details');
      setIsModalVisible(false);
    } finally {
      setModalLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleSave = async (values) => {
    const payload = {
      id: editingUser?.id,
      username: values.username,
      password: values.password,
      first_name: values.first_name,
      last_name: values.last_name,
      email: values.email,
      perms: {
        dashboard: values.dashboard || false,
        media: values.media || false,
        playlists: values.playlists || false,
        video_editor: values.video_editor || false,
        machines: values.machines || false,
        mappings: values.mappings || false,
        telegram: values.telegram || false,
        users: values.users || false
      }
    };

    try {
      if (editingUser) {
        await axios.put(USERS_URL, payload);
        message.success('User updated successfully');
      } else {
        await axios.post(USERS_URL, payload);
        message.success('User created successfully');
      }
      setIsModalVisible(false);
      setEditingUser(null);
      form.resetFields();
      fetchUsers();
    } catch (error) {
      message.error(error.response?.data?.error || 'Action failed');
    }
  };

  const columns = [
    {
      title: 'User',
      key: 'user_info',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <span 
            onClick={() => fetchUserDetails(record)}
            style={{ color: '#3b82f6', fontWeight: 'bold', cursor: 'pointer', textDecoration: 'underline' }}
          >
            <UserOutlined style={{ marginRight: '8px' }} />
            {record.username}
          </span>
          <div style={{ fontSize: '11px', color: '#94a3b8', paddingLeft: '22px' }}>
            {record.first_name} {record.last_name}
          </div>
        </Space>
      ),
    },
    {
        title: 'Email',
        dataIndex: 'email',
        key: 'email',
        render: (text) => <span style={{ color: '#94a3b8', fontSize: '12px' }}>{text || '-'}</span>
    },
    {
      title: 'Role / Permissions',
      key: 'status',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          {record.is_superuser ? (
            <Badge status="warning" text={<span style={{ color: '#fbbf24', fontWeight: 'bold' }}>ADMIN (Full Access)</span>} />
          ) : (
            <>
              <Badge status="processing" text={<span style={{ color: '#3b82f6' }}>OPERATOR</span>} />
              <div style={{ fontSize: '10px', color: '#64748b' }}>
                Access: {Object.entries(record.perms || {})
                  .filter(([_, v]) => v)
                  .map(([k]) => k.toUpperCase())
                  .join(' | ')}
              </div>
            </>
          )}
        </Space>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          <Button 
            size="small" 
            icon={<EditOutlined />} 
            onClick={() => fetchUserDetails(record)}
            style={{ background: '#1e293b', color: '#94a3b8', border: '1px solid #334155' }} 
          >Edit</Button>
          {!record.is_superuser && (
            <Popconfirm title="Remove user?" onConfirm={() => axios.delete(`/api/users/delete/${record.id}/`).then(fetchUsers)}>
              <Button 
                size="small" 
                icon={<DeleteOutlined />} 
                style={{ background: '#1e293b', color: '#94a3b8', border: '1px solid #334155' }} 
              >
                Delete
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '0px' }}>
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }} align="stretch">
        <Col span={12}>
          <div style={{ background: '#1e293b', padding: CARD_PADDING, borderRadius: '8px', border: '1px solid #334155', borderBottom: '3px solid #3b82f6', height: '100%', display: 'flex', alignItems: 'center' }}>
            <Space size="middle">
              <TeamOutlined style={{ color: '#3b82f6', fontSize: '24px' }} />
              <div>
                <div style={{ color: '#64748b', fontSize: '12px' }}>TOTAL USERS</div>
                <div style={{ color: 'white', fontSize: '24px', fontWeight: 'bold' }}>{users.length}</div>
              </div>
            </Space>
          </div>
        </Col>
        <Col span={12}>
          <div 
            onClick={() => { setEditingUser(null); setIsModalVisible(true); form.resetFields(); }} 
            style={{ background: '#1e293b', padding: CARD_PADDING, borderRadius: '8px', border: '1px solid #334155', borderBottom: '3px solid #3b82f6', cursor: 'pointer', height: '100%', display: 'flex', alignItems: 'center' }}
          >
            <Space size="middle">
              <UserAddOutlined style={{ color: '#3b82f6', fontSize: '24px' }} />
              <div>
                <div style={{ color: '#3b82f6', fontSize: '12px', fontWeight: 'bold' }}>ADD NEW OPERATOR</div>
                <div style={{ color: 'white', fontSize: '14px' }}>Set custom access</div>
              </div>
            </Space>
          </div>
        </Col>
      </Row>

      <div style={{ background: 'rgba(30, 41, 59, 0.3)', padding: '24px', borderRadius: '12px', border: '1px solid #1e293b' }}>
        <Table dataSource={users} columns={columns} rowKey="id" loading={loading} pagination={{ pageSize: 15 }} style={{ background: 'transparent' }} />
      </div>

      <EditBoxModal 
        title={editingUser ? "Edit Operator Access" : "Create New Operator"} 
        isVisible={isModalVisible} 
        onOk={() => form.submit()} 
        onCancel={() => { setIsModalVisible(false); setEditingUser(null); form.resetFields(); }}
        destroyOnClose
      >
        <Spin spinning={modalLoading}>
          <Form 
              form={form} 
              layout="vertical" 
              onFinish={handleSave}
              initialValues={{ 
                dashboard: true,
                media: true, 
                playlists: true, 
                video_editor: true
              }}
          >
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item name="username" label={<span style={{ color: '#94a3b8' }}>Username</span>} rules={[{ required: true }]}><Input disabled={!!editingUser} style={{ background: '#0f172a', color: 'white', border: '1px solid #334155' }} /></Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="first_name" label={<span style={{ color: '#94a3b8' }}>First Name</span>}><Input style={{ background: '#0f172a', color: 'white', border: '1px solid #334155' }} /></Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="last_name" label={<span style={{ color: '#94a3b8' }}>Last Name</span>}><Input style={{ background: '#0f172a', color: 'white', border: '1px solid #334155' }} /></Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item name="email" label={<span style={{ color: '#94a3b8' }}>Email Address</span>} rules={[{ type: 'email' }]}><Input prefix={<MailOutlined style={{ color: '#64748b' }} />} style={{ background: '#0f172a', color: 'white', border: '1px solid #334155' }} /></Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item name="password" label={<span style={{ color: '#94a3b8' }}>{editingUser ? "New Password (leave blank to keep old)" : "Password"}</span>} rules={[{ required: !editingUser }]}><Input.Password style={{ background: '#0f172a', color: 'white', border: '1px solid #334155' }} /></Form.Item>
              </Col>
            </Row>
            
            <Divider orientation="left" style={{ borderColor: '#334155', color: '#3b82f6', fontSize: '12px' }}>
              <LockOutlined /> PAGE PERMISSIONS
            </Divider>

            <Row gutter={[16, 8]}>
              <Col span={12}><Form.Item name="dashboard" valuePropName="checked"><Checkbox style={{ color: '#94a3b8' }}>Dashboard</Checkbox></Form.Item></Col>
              <Col span={12}><Form.Item name="media" valuePropName="checked"><Checkbox style={{ color: '#94a3b8' }}>Media Library</Checkbox></Form.Item></Col>
              <Col span={12}><Form.Item name="playlists" valuePropName="checked"><Checkbox style={{ color: '#94a3b8' }}>Playlists</Checkbox></Form.Item></Col>
              <Col span={12}><Form.Item name="video_editor" valuePropName="checked"><Checkbox style={{ color: '#94a3b8' }}>Video Editor</Checkbox></Form.Item></Col>
              <Col span={12}><Form.Item name="machines" valuePropName="checked"><Checkbox style={{ color: '#94a3b8' }}>Register Machine</Checkbox></Form.Item></Col>
              <Col span={12}><Form.Item name="mappings" valuePropName="checked"><Checkbox style={{ color: '#94a3b8' }}>Mappings</Checkbox></Form.Item></Col>
              <Col span={12}><Form.Item name="telegram" valuePropName="checked"><Checkbox style={{ color: '#94a3b8' }}>Telegram Users</Checkbox></Form.Item></Col>
              <Col span={12}><Form.Item name="users" valuePropName="checked"><Checkbox style={{ color: '#94a3b8' }}>User Management</Checkbox></Form.Item></Col>
            </Row>
          </Form>
        </Spin>
      </EditBoxModal>

      <style>{`
        .ant-table { background: transparent !important; color: #94a3b8 !important; }
        .ant-table-thead > tr > th { background: transparent !important; color: #64748b !important; border-bottom: 1px solid #1e293b !important; text-transform: uppercase; font-size: 11px; }
        .ant-table-tbody > tr > td { border-bottom: 1px solid #1e293b !important; }
        .ant-table-tbody > tr:hover > td { background: rgba(255,255,255,0.02) !important; }
        .ant-checkbox-wrapper { color: #94a3b8 !important; font-size: 12px; }
        .ant-divider-inner-text { padding: 0 10px !important; background: transparent !important; }
        .ant-input-password { background: #0f172a !important; border: 1px solid #334155 !important; }
        .ant-input-password input { background: transparent !important; color: white !important; }
      `}</style>
    </div>
  );
};

export default Users;